/**
 * This manages YouTubes videos on the page and sets up analytics for them.
 *
 * Custom events:
 *   youTubePlayerAPIReady.Site      Sent when the YouTube API is ready.
 *   youTubePlayerReady.Site         Sent when a YouTube Player instance is ready.
 *   youTubePlayerError.Site         Sent when a YouTube Player error occurs.
 *   youTubePlayerStateChange.Site   Sent when a YouTube Player's state occurs.
 *
 */

/*
TODO:
  YouTube event tracking doesn't yet work with the new Gutenberg blocks
  Need to retest all code after conversion to ES6

 */

import {$body} from './references.js';
import * as analytics from './analytics.js';

// console.log('Site:youtube');


/**
 * The YouTube Player API will call this function when it is ready
 * Required for YouTube to work. Must be global.
 *
 * @since 0.1.0
 */
window.onYouTubePlayerAPIReady = onYouTubePlayerAPIReady;


/**
 * Sets up defaults, analytics, and utilities for YouTube videos.
 *
 * Analytics events:
 *   Video Playing
 *   Video Paused
 *   Video Progress
 *   Video Ended
 *
 * @since 0.1.0
 */

const queue            = []
    , videos           = {}    // Active YouTube Player instances
    , progressInterval = 30000 // ms between reporting playback progress
;

export let isAPIReady = false;

let lastAutoId = 0 // eslint-disable-line


addAnalyticsToEmbedShortcodeVideos();


/**
 * Adds analytics to YouTube videos add via shortcode.
 *
 * @since 0.1.0
 */
function addAnalyticsToEmbedShortcodeVideos() {
  // console.log('Site:youtube:addAnalyticsToEmbedShortcodeVideos');

  // Add event tracking for videos inserted via the [embed] shortcode
  const $videos = $body.find('iframe[data-embed-youtube]');

  $videos.each(function (index, element) {
    const $this = jQuery(element)
        , id    = 'youtube-video-auto-id-' + lastAutoId++
        , args  = {
          trackingOnly : true,
        }
    ;

    $this.attr('id', id);
    initializeVideo(id, extractVideoId($this.attr('src')), args);
  });
}


/**
 * Called when the YouTube API Player is ready. Emits an event so everyone else can listen in.
 *
 * @since 0.1.0
 */
function onYouTubePlayerAPIReady() {
  // console.log('Site:youtube:onYouTubePlayerAPIReady');
  // console.log('this', this);

  isAPIReady = true;

  $body.trigger('youTubePlayerAPIReady.Site');
  processQueue();
}


/**
 * Called when the YouTube IFrame API is ready. Emits an event so everyone else can listen in.
 *
 * @since 0.1.0
 */
function onYouTubeIframeAPIReady() { // eslint-disable-line
  // console.log('Site:youtube:onYouTubeIframeAPIReady');
  // console.log('this', this);

  $body
    .trigger('youTubeIFrameAPIReady.Site')
    .find('.oembed.youtube > iframe').each(function (index, element) {
      const $this = jQuery(element);

      let id = $this.attr('id');

      if (!id) {
        id = 'oembed-youtube-' + index;
        $this.attr('id', id);
      }

      initializeVideo(id, $this.attr('src'));
    })
  ;
}


/**
 * Initializes the video immediately if the YouTube API is ready, otherwise it
 * is queued.
 *
 * @since 0.1.0
 *
 * @param string elementId    The DOM element ID to initialize the video in.
 * @param string videoIdOrURL The YouTube video id or URL containing video id.
 * @param array  args         Args to be merged with the default args. See initiliazePlayer for options.
 */
function initializeVideo(elementId, videoIdOrURL, args) {
  // console.log('Site:youtube:initializeVideo', elementId, videoIdOrURL);

  queue.push({
    elementId : elementId,
    videoId   : extractVideoId(videoIdOrURL),
    args      : args ? args : {},
  });

  if (isAPIReady) {
    processQueue();
  }
}


/**
 * Initializes any videos in the queue.
 *
 * @since 0.1.0
 */
function processQueue() {
  // console.log('Site:youtube:processQueue');

  if (!queue.length) {
    // console.log('No items in the queue');
    return;
  }

  // console.log('Items in the queue');

  Object.entries(queue).forEach(([key, video]) => {
    delete queue[key];

    initializePlayer(video);
  });
}


/**
 * Initializes the YouTube player.
 *
 * Setup up event listenenrs and pushes events to the data lyer.
 *
 * @since 0.1.0
 *
 * @param  object video Video object containing elementId and videoId.
 */
function initializePlayer(video) {
  // console.log('Site:youtube:initializePlayer', video);

  // https://developers.google.com/youtube/iframe_api_reference
  let args = {
    width      : '640',
    height     : '390',
    videoId    : video.videoId,
    // https://developers.google.com/youtube/player_parameters?playerVersion=HTML5
    // There are some other options not listed here
    playerVars : {
      autoplay       : 0,  // Doesn't work in iOS and other mobile environments
      enablejsapi    : 1,  // Enables player to be controlled via IFrame or JS PlayerAPI calls
      modestbranding : 1,  // 1 = removes most YouTube branding
      controls       : 2,  // 0 = no controls, 1 & 2 = show controls (see docs for subtle difference)
      // start          : 30, // Time in seconds when the video should start playing
      // end            : 60, // Time in seconds when the video should stop playing
      // fs             : 1,  // 0 = disables full screen mode
      // disablekb      : 0,  // 1 = disable keyboard controls
      // cc_load_policy : 0,  // 1 = show closed captions by default, 0 = off
      // iv_load_policy : 1,  // 1 = show video annotations, 3 = disable video annotations
      // color          : 'red',  // Controls the progress bar color. Options are red and white. Disables 'modestbranding' option.
      // origin         : 'buckthequo.dev',  // Extra security for the IFrame API
      // loop           : 0,  // 1 = loop video, 0 = play once. Note: Must also set playlist: <videoId> for this to work.
      // playlist       : '', // CSV list of video ids to play
      // playsinline    : 0,  // Controls whether videos play inline or fullscreen in an HTML5 player on iOS
      // rel            : 1,  // 1 = show related videos at end of playback, 0 = don't
      // showinfo       : 1,  // 1 = show video info before video starts playing, 0 = don't
    },
    events : {
      onReady       : onPlayerReady,
      onStateChange : onPlayerStateChange,
      onError       : onPlayerError,
      // onPlaybackQualityChange
      // onPlaybackRateChange
      // onApiChange
    },
  };

  args = jQuery.extend(true, args, video.args);

  // console.log('args', args);

  // If the video isn't loading on a dynamically added video, you may need to use a setTimeout() to wait
  // 1 cycle for the DOM to update first
  addVideoToList(video.videoId, video.elementId, new YT.Player(video.elementId, args));
}


/**
 * Adds a YouTube video to the list of videos being managed.
 *
 * You can also use this if you need to initialize the YT.Player yourself, or by a 3rd party,
 * but still want the standard YouTube tracking done.  You'll still need to add the event callbacks
 * yourself as done above.
 *
 * @since 0.1.0
 *
 * @param string videoId   YouTube video id.
 * @param string elementId Container element id..
 * @param object player    YouTube player instance.
 */
export function addVideoToList(videoId, elementId, player) {
  // console.log('Site:youtube:addVideoToList', videoId);

  // If the video already exists, disable any existing tracking
  if (videos[videoId]) {
    // console.log('Already exists');
    stopPlaybackProgressTracking(videos[videoId]);
  }

  videos[videoId] = {
    player           : player,
    elementId        : elementId,
    progressInterval : {
      id          : false,
      startTime   : 0,
      elapsedTime : 0,
    },
  };
}


/**
 * Removes a YouTube video from the list of videos being managed. This assumes
 * that a videoId is only used once per page.
 *
 * Removes any video event listener/APi hooks before destroying.
 *
 * @since 0.1.0
 *
 * @param string videoId YouTube video id.
 */
export function removeVideoFromList(videoId) {
  // console.log('Site:youtube:removeVideoFromList', videoId);

  // If the video already exists, disable any existing tracking
  if (!videos[videoId]) {
    console.error('Video ID Not Found To Remove');

    return;
  }

  stopPlaybackProgressTracking(videos[videoId]);

  delete videos[videoId];
}


/**
 * Called when the YouTube Player instance is ready.
 *
 * @since 0.1.0
 *
 * @param object event Holds a reference to the player.
 */
export function onPlayerReady(event) {
  // console.log('Site:youtube:onPlayerReady');

  // event.target.playVideo();
  $body.trigger('youTubePlayerReady.Site', event);
}


/**
 * Called when the YouTube Player instance errors.
 *
 * @since 0.1.0
 *
 * @param object event Event data.
 */
export function onPlayerError(event) {
  // console.log('Site:youtube:onPlayerError');

  console.error('YouTube video error:', event.data);
  console.log(event.target.getDebugText());

  $body.trigger('youTubePlayerError.Site', event);
}


/**
 * Called when the YouTube Player instance state has changed.
 *
 * @since 0.1.0
 *
 * @param object event Holds a reference to the player and the current state.
 */
export function onPlayerStateChange(event) {
  // console.log('Site:youtube:onPlayerStateChange');
  // console.log(event);

  try {
    const videoData = getVideoDataFromPlayer(event.target)
        , video     = getVideoById(videoData['video_id'])
    ;

    // console.log('video', video);

    switch (event.data) {
      case YT.PlayerState.UNSTARTED:
        stopPlaybackProgressTracking(video);
        break;

      case YT.PlayerState.CUED:
        stopPlaybackProgressTracking(video);
        break;

      case YT.PlayerState.BUFFERING:
        stopPlaybackProgressTracking(video);
        break;

      case YT.PlayerState.PLAYING:
        track(event.target, 'Video Playing');
        startPlaybackProgressTracking(video);
        break;

      case YT.PlayerState.PAUSED:
        track(event.target, 'Video Paused');
        stopPlaybackProgressTracking(video);
        break;

      case YT.PlayerState.ENDED:
        track(event.target, 'Video Ended');
        stopPlaybackProgressTracking(video);
        break;
    }
  } catch (error) {
    return;
  }

  $body.trigger('youTubePlayerStateChange.Site', event);
}


function startPlaybackProgressTracking(video) {
  // console.log('Site:youtube:startPlaybackProgressTracking');

  setPlaybackProgressTimeout(video);
}


function stopPlaybackProgressTracking(video) {
  // console.log('Site:youtube:stopPlaybackProgressTracking');

  if (video.progressInterval.id !== false) {
    clearInterval(video.progressInterval.id);

    video.progressInterval.id = false;
    video.progressInterval.elapsedTime = Number(new Date()) - video.progressInterval.startTime;
  }
}


function setPlaybackProgressTimeout(video) {
  // console.log('Site:youtube:setPlaybackProgressTimeout');
  // console.log('video', video);

  if (video.progressInterval.id !== false) {
    stopPlaybackProgressTracking(video);
  }

  // Scale the interval so it goes off every 30 seconds of playtime, even if the video is paused/buffers
  let interval = progressInterval - video.progressInterval.elapsedTime;

  if (interval <= 0) {
    interval = progressInterval;
  }

  // console.log('interval', interval);

  video.progressInterval.startTime = Number(new Date());
  video.progressInterval.id = setTimeout(onVideoPlaybackProgressTimeout, interval, video);
}


function onVideoPlaybackProgressTimeout(video) {
  // console.log('Site:youtube:onVideoPlaybackProgressTimeout');
  // console.log('video', video);

  video.progressInterval.id = false;
  video.progressInterval.elapsedTime = 0;

  track(video.player, 'Video Progress');
  setPlaybackProgressTimeout(video);
}


/**
 * Tracks a video event, adding common data to it.
 *
 * @since 0.1.0
 *
 * @param  object player    The player instance.
 * @param  string eventName Name of the event.
 */
function track(player, eventName) {
  // console.log('Site:youtube:track', eventName);

  let videoData, video;

  try {
    videoData = getVideoDataFromPlayer(player);
    video     = getVideoById(videoData['video_id']);
  } catch (error) {
    return;
  }

  const $element  = jQuery('#' + video.elementId)
      , eventData = {
        id             : videoData['video_id'],
        containerId    : $element.closest('[id]').attr('id'),
        containerClass : $element.closest('[class]').attr('class'),
        title          : videoData['title']         ? videoData['title']         : '',
        quality        : videoData['video_quality'] ? videoData['video_quality'] : '',
        author         : videoData['author']        ? videoData['author']        : '',
        page           : siteData.page,
      }
  ;

  if (video) {
    eventData.time            = parent.round(getCurrentTimeFromPlayer(video.player), 2);
    eventData.roundedTime     = Math.round(eventData.time / (progressInterval / 1000)) * progressInterval / 1000;
    eventData.duration        = getDurationFromPlayer(video.player);
    eventData.playbackPercent = parent.round(eventData.time / eventData.duration * 100, 2);
  }

  // console.log('data', data);

  analytics.track(eventName, eventData);
}


/**
 * Returns the video object of an initialized video by its video id.
 *
 * @since 0.1.0
 *
 * @param  string videoId Video ID.
 *
 * @return object The video object.
 *
 * @throws Errors.
 */
function getVideoById(videoId) {
  // console.log('Site:youtube:getVideoById', videoId);

  if (videos[videoId]) {
    return videos[videoId];
  }

  throw new Error('Video ID Not Found');
}


/**
 * Returns a value from the player where the method to get said value may not reliably be available,
 * either due to timing issues or using undocumented features which are sometimes removed/re-added (ie, getVideoData).
 *
 * Tries the method first, otherwise searches for the specified property.
 *
 * @since 0.1.0
 *
 * @param  object player   YouTube Player object.
 * @param  string method   Name of the method normally used to retrieve the value.
 * @param  string property Name of the property where the value is stored.
 * @param  string type     The type of the property, 'string', 'object', 'number'. Used to compare with typeof.
 *
 * @return mixed Data.
 *
 * @throws Error if not found.
 */
function getValueFromPlayer(player, method, property, type) {
  // console.log('Site:youtube:getValueFromPlayer');

  if (typeof player[method] === 'function') {
    // console.log('Method found');
    return player[method]();
  }

  // In the current compiled version of the YT Player, the data is stored under `j`
  // Check there first before checking the everything
  if (typeof player.j === 'object' && typeof player.j[property] === type) {
    // console.log('object.j[property] found');
    return player.j[property];
  }

  // console.log('Checking everything');

  // Check the entire object for the data
  for (const prop of player) {
    if (typeof player[prop] !== 'object' || player[prop] === null) {
      continue;
    }

    if (typeof player[prop][property] === type) {
      return player[prop][property];
    }
  }

  throw new Error('Player Value Not Found');
}


/**
 * Returns the videoData object from the YouTube Player object.
 *
 * @since 0.1.0
 *
 * @param  object player event.target object.
 *
 * @return object videoData object.
 *
 * @throws Errors.
 */
function getVideoDataFromPlayer(player) {
  // console.log('Site:youtube:getVideoDataFromPlayer');

  return getValueFromPlayer(player, 'getVideoData', 'videoData', 'object');
}


/**
 * Returns the currentTime object from the YouTube Player object.
 *
 * @since 0.1.0
 *
 * @param  object object player object.
 *
 * @return object videoData object.
 *
 * @throws Errors.
 */
function getCurrentTimeFromPlayer(player) {
  // console.log('Site:youtube:getCurrentTimeFromPlayer');

  return getValueFromPlayer(player, 'getCurrentTime', 'currentTime', 'number');
}


/**
 * Returns the duration object from the YouTube Player object.
 *
 * @since 0.1.0
 *
 * @param  object object player object.
 *
 * @return object videoData object.
 *
 * @throws Errors.
 */
function getDurationFromPlayer(player) {
  // console.log('Site:youtube:getDurationFromPlayer');

  return getValueFromPlayer(player, 'getDuration', 'duration', 'number');
}


/**
 * Extracts the YouTube video id from a URL if found, otherwise returns the string.
 *
 * @since 0.1.0
 *
 * @param  string input String containing a video id.
 *
 * @return string Video id or original string.
 */
export function extractVideoId(input) {
  // console.log('Site:youtube:extractVideoId');
  // console.log('input', input);

  // Viewing URL: https://www.youtube.com/watch?v=gV79eObat24
  // Embed URL:   https://www.youtube.com/watch?v=gV79eObat24
  // Embed URL:   https://www.youtube.com/embed/gV79eObat24
  // Embed URL:   https://www.youtube-nocookie.com/embed/LaLvVc1sS20?rel=0&showinfo=0
  // Short URL:   https://youtu.be/gV79eObat24
  // URL in HTML: <iframe width="560" height="315" src="https://www.youtube.com/embed/gV79eObat24" frameborder="0" allowfullscreen></iframe>

  const matches = input.match(/(?:youtube(?:-nocookie)?\.com\/(?:(?:watch|embed)\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]+)/);

  // console.log('matches', matches);

  if (matches && matches.length === 2) {
    return matches[1];
  }

  return input;
}

